/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { buildUrl } from '@salesforce/storefront-next-runtime/site-context';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { TrackOrderLookupForm } from '@/components/track-order';

/**
 * Third option alongside Login / Create Account: guest order lookup, reusing the
 * `/track-order` page's own form and action so the lookup logic isn't duplicated. The form's
 * `action` is built with the site/locale prefix since it posts across routes (this page → the
 * `/track-order` route's `action`), the same pattern `StandardLoginForm` uses for its own submit.
 */
export function GuestOrderLookupPanel() {
    const { t } = useTranslation('trackOrder');
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();
    const trackOrderActionPath = buildUrl({
        to: '/track-order',
        urlConfig: config.url,
        params: { siteId: siteRef, localeId: localeRef },
    });

    return (
        <div>
            <h2 className="pb-3 border-b border-muted-foreground/10 text-sm font-bold uppercase tracking-[0.12em] text-foreground">
                {t('embedded.title')}
            </h2>
            <p className="mt-4 text-sm text-muted-foreground">{t('embedded.description')}</p>
            <div className="mt-6">
                <TrackOrderLookupForm action={trackOrderActionPath} />
            </div>
        </div>
    );
}
